import { createContext, useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Loading } from '../components/ui';
import { fetchHomePageContent, HomePageContent } from '../services/api';

export type PublicPageVisibilityFlag =
  | 'showAboutPage'
  | 'showPortfolioPage'
  | 'showServicesPage'
  | 'showCareersPage'
  | 'showNewsPage'
  | 'showContactPage'
  | 'showFaqPage'
  | 'showQuotePage'
  | 'showLegalPage';

interface SiteVisibilityContextValue {
  content: HomePageContent | null;
  loading: boolean;
  error?: string;
}

const SiteVisibilityContext = createContext<SiteVisibilityContextValue | undefined>(undefined);

export const SiteVisibilityProvider = ({ children }: { children: React.ReactNode }) => {
  const [content, setContent] = useState<HomePageContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    const abortController = new AbortController();

    fetchHomePageContent()
      .then((data) => {
        if (!abortController.signal.aborted) {
          setContent(data);
        }
      })
      .catch((err) => {
        if (!abortController.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Unable to load site configuration');
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <SiteVisibilityContext.Provider value={{ content, loading, error }}>
      {children}
    </SiteVisibilityContext.Provider>
  );
};

export const useSiteVisibility = () => {
  const context = useContext(SiteVisibilityContext);
  if (!context) {
    throw new Error('useSiteVisibility must be used within a SiteVisibilityProvider');
  }
  return context;
};

export const PageVisibilityRoute = ({
  pageFlag,
  children,
}: {
  pageFlag: PublicPageVisibilityFlag;
  children: React.ReactNode;
}) => {
  const { content, loading, error } = useSiteVisibility();

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Navigate to="/" replace />;
  }

  const isVisible = content?.[pageFlag] === true;
  return isVisible ? <>{children}</> : <Navigate to="/" replace />;
};
